import { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { BarChart2, Facebook, Camera, Music, Plus, X } from "lucide-react";
import { Hash, Tag, Key, PencilSimple, Trash, Megaphone } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import {
  PageHeader, Input, Select, Checkbox, Button, Table, THead, TBody, TR, TH, TD,
  Badge, Skeleton, EmptyState, Tooltip,
} from "../../components/ui";

/* Each platform has its own ID format — the form adapts (label, placeholder,
   validation, help) to the selected platform. */
const PLATFORMS = {
  google: {
    title: "Google Analytics 4",
    tint: "bg-red-50",
    icon: BarChart2,
    idLabel: "Measurement ID (GA4)",
    placeholder: "G-XXXXXXXXXX",
    pattern: /^G-[A-Z0-9]{6,14}$/i,
    formatHint: "Format : G-XXXXXXXXXX",
    help: "Google Analytics → Administration → Flux de données → votre flux web.",
    tokenLabel: null,
  },
  facebook: {
    title: "Meta Pixel (Facebook / Instagram)",
    tint: "bg-blue-50",
    icon: Facebook,
    idLabel: "Pixel ID (Meta)",
    placeholder: "1234567890123456",
    pattern: /^\d{15,16}$/,
    formatHint: "Format : 15–16 chiffres",
    help: "Meta Events Manager → Sources de données → votre pixel.",
    tokenLabel: "Token Conversions API (facultatif)",
  },
  snapchat: {
    title: "Snap Pixel",
    tint: "bg-yellow-50",
    icon: Camera,
    idLabel: "Pixel ID (Snap)",
    placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
    pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    formatHint: "Format : UUID (8-4-4-4-12)",
    help: "Snapchat Ads Manager → Events Manager.",
    tokenLabel: "Token Conversions API (facultatif)",
  },
  tiktok: {
    title: "TikTok Pixel",
    tint: "bg-surface-2",
    icon: Music,
    idLabel: "Pixel ID (TikTok)",
    placeholder: "C0XXXXXXXXXXXXXXXXXX",
    pattern: /^[A-Z0-9]{16,24}$/i,
    formatHint: "Format : code alphanumérique (16–24 caractères)",
    help: "TikTok Ads Manager → Outils → Événements.",
    tokenLabel: "Token Events API (facultatif)",
  },
};

const CONVERSION_TYPES = {
  google: ["Page view", "Purchase", "Sign up", "Lead"],
  facebook: ["Purchase", "Lead", "Complete registration", "Add to cart"],
  snapchat: ["Purchase", "Sign up", "Add to cart", "View content"],
  tiktok: ["View content", "Add to cart", "Purchase", "Generate lead"],
};

const emptyForm = {
  id: null,
  platform: "facebook",
  pixel_id: "",
  label: "",
  conversion_type: "",
  access_token: "",
  is_active: true,
};

export default function PixelSettings() {
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null); // null = closed, object = open

  const load = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post("/pixels/search", {});
      setPixels(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Failed to load pixels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => setForm({ ...emptyForm });
  const openEdit = (p) =>
    setForm({
      id: p.id,
      platform: p.platform,
      pixel_id: p.pixel_id,
      label: p.label || "",
      conversion_type: p.conversion_type || "",
      access_token: p.access_token || "",
      is_active: !!p.is_active,
    });

  const save = async (e) => {
    e.preventDefault();
    const meta = PLATFORMS[form.platform] || {};
    const id = form.pixel_id.trim();
    if (!id) {
      toast.warn(`${meta.idLabel || "Pixel ID"} est requis`);
      return;
    }
    if (meta.pattern && !meta.pattern.test(id)) {
      toast.warn(`${meta.idLabel || "Pixel ID"} invalide — ${meta.formatHint}`);
      return;
    }
    try {
      setSaving(true);
      if (form.id) {
        await axiosInstance.put(`/pixels/update/${form.id}`, form);
      } else {
        await axiosInstance.post("/pixels/store", form);
      }
      toast.success("Pixel saved");
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save pixel");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axiosInstance.delete(`/pixels/destroy/${id}`);
      toast.success("Pixel deleted");
      setPixels((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      toast.error("Failed to delete pixel");
    }
  };

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Pixels & Tracking"
        subtitle="Chaque plateforme a son propre identifiant : GA4 (G-…), Meta Pixel (chiffres), Snap (UUID), TikTok (code). Le formulaire s'adapte à la plateforme choisie."
        actions={
          <Button onClick={openCreate} icon={Plus}>
            Add pixel
          </Button>
        }
      />

      {/* Platform overview */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(PLATFORMS).map(([key, meta]) => {
          const existing = pixels.find((p) => p.platform === key);
          const Icon = meta.icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => (existing ? openEdit(existing) : setForm({ ...emptyForm, platform: key }))}
              className="flex items-start gap-3 rounded-xl border border-line bg-surface p-4 text-left transition hover:border-line-strong hover:shadow-soft"
            >
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line ${meta.tint}`}>
                <Icon className="h-4.5 w-4.5 text-ink" style={{ width: 18, height: 18 }} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink">{meta.title}</span>
                {existing ? (
                  <span className="mt-0.5 flex items-center gap-1.5">
                    <Badge tone={existing.is_active ? "success" : "neutral"} dot>
                      {existing.is_active ? "Actif" : "Inactif"}
                    </Badge>
                    <span className="truncate font-mono text-xs text-muted">{existing.pixel_id}</span>
                  </span>
                ) : (
                  <span className="mt-0.5 block text-xs text-faint">Non configuré — cliquer pour ajouter</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Platform</TH>
            <TH>Pixel ID</TH>
            <TH>Label</TH>
            <TH>Conversion</TH>
            <TH>Status</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                <TD><div className="flex items-center gap-2.5"><Skeleton className="h-8 w-8 rounded-lg" /><Skeleton className="h-3.5 w-28" /></div></TD>
                <TD><Skeleton className="h-3.5 w-24" /></TD>
                <TD><Skeleton className="h-3.5 w-20" /></TD>
                <TD><Skeleton className="h-3.5 w-20" /></TD>
                <TD><Skeleton className="h-5 w-16 rounded-full" /></TD>
                <TD><Skeleton className="ml-auto h-7 w-16" /></TD>
              </TR>
            ))}

          {!loading &&
            pixels.map((p) => {
              const meta = PLATFORMS[p.platform] || {};
              const Icon = meta.icon || BarChart2;
              return (
                <TR key={p.id}>
                  <TD>
                    <div className="flex items-center gap-2.5">
                      <span className={`grid h-8 w-8 place-items-center rounded-lg border border-line ${meta.tint || "bg-surface-2"}`}>
                        <Icon className="h-4 w-4 text-ink" />
                      </span>
                      <span className="font-medium text-ink">{meta.title || p.platform}</span>
                    </div>
                  </TD>
                  <TD className="font-mono text-muted">{p.pixel_id}</TD>
                  <TD className="text-muted">{p.label || "—"}</TD>
                  <TD className="text-muted">{p.conversion_type || "—"}</TD>
                  <TD>
                    <Badge tone={p.is_active ? "success" : "neutral"} dot>
                      {p.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TD>
                  <TD>
                    <div className="flex items-center justify-end gap-0.5">
                      <Tooltip label="Edit">
                        <button onClick={() => openEdit(p)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                          <PencilSimple size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <button onClick={() => remove(p.id)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600">
                          <Trash size={16} />
                        </button>
                      </Tooltip>
                    </div>
                  </TD>
                </TR>
              );
            })}

          {!loading && pixels.length === 0 && (
            <tr>
              <td colSpan={6}>
                <EmptyState
                  icon={Megaphone}
                  title="No pixels yet"
                  description="Add a tracking pixel to start measuring conversions."
                  action={
                    <Button icon={Plus} onClick={openCreate}>
                      Add pixel
                    </Button>
                  }
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      {/* Form modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-surface shadow-2xl">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="text-base font-semibold text-ink">
                {form.id ? "Edit pixel" : "Add pixel"}
              </h2>
              <button onClick={() => setForm(null)} className="rounded-lg p-2 hover:bg-surface-2" aria-label="Close">
                <X className="w-5 h-5 text-muted" />
              </button>
            </div>

            <form onSubmit={save} className="px-5 py-4 space-y-4">
              <Select
                label="Platform"
                value={form.platform}
                onChange={(v) => setForm((f) => ({ ...f, platform: v, conversion_type: "", access_token: "" }))}
                options={Object.entries(PLATFORMS).map(([key, { title }]) => ({ value: key, label: title }))}
              />
              {(() => {
                const meta = PLATFORMS[form.platform] || {};
                return (
                  <>
                    <Input
                      label={meta.idLabel || "Pixel ID"}
                      icon={Hash}
                      value={form.pixel_id}
                      onChange={(e) => setForm((f) => ({ ...f, pixel_id: e.target.value }))}
                      placeholder={meta.placeholder}
                      hint={`${meta.formatHint} · ${meta.help}`}
                    />
                    <Input
                      label="Label (optionnel)"
                      icon={Tag}
                      value={form.label}
                      onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                      placeholder={`Ex. ${meta.title} principal`}
                    />
                    <Select
                      label="Conversion type"
                      placeholder="Select a type"
                      value={form.conversion_type || null}
                      onChange={(v) => setForm((f) => ({ ...f, conversion_type: v || "" }))}
                      options={(CONVERSION_TYPES[form.platform] || []).map((t) => ({ value: t, label: t }))}
                    />
                    {meta.tokenLabel ? (
                      <Input
                        label={meta.tokenLabel}
                        icon={Key}
                        value={form.access_token}
                        onChange={(e) => setForm((f) => ({ ...f, access_token: e.target.value }))}
                        placeholder="Pour le suivi côté serveur (Conversions API)"
                      />
                    ) : null}
                  </>
                );
              })()}
              <Checkbox
                label="Active"
                checked={form.is_active}
                onChange={(val) => setForm((f) => ({ ...f, is_active: val }))}
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" type="button" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" loading={saving}>
                  Save pixel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
